import { useEffect, useRef } from 'react';

import { StylesProvider } from '@material-ui/core/styles';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import { useTranslation } from 'react-i18next';

import { setActiveMfaTab } from 'actions/account';
import { ENABLE_2FA_TABS } from 'components/security/constants';
import useCustomMediaQuery from 'hooks/useMediaQuery';
import { selectMfaActiveTab } from 'selectors/account';
import { useAppDispatch, useAppSelector } from 'storeHooks';

import styles from './securityTabs.module.scss';

type SecurityTabType = (typeof ENABLE_2FA_TABS)[keyof typeof ENABLE_2FA_TABS];

const SecurityTabs = () => {
  const { t } = useTranslation(['account']);
  const { whenAboveMd } = useCustomMediaQuery();
  const activeTab = useAppSelector(selectMfaActiveTab);
  const dispatch = useAppDispatch();

  const setActiveTab = (tabId: SecurityTabType) => {
    dispatch(setActiveMfaTab(tabId));
  };

  const tabList = [
    {
      id: ENABLE_2FA_TABS.DOWNLOAD_APP,
      label: t('account:security.biometricAuthentication.downloadAuthApp'),
    },
    {
      id: ENABLE_2FA_TABS.SCAN_QR,
      label: t('account:security.scanQrCode'),
    },
    {
      id: ENABLE_2FA_TABS.BACKUP_KEY,
      label: t('account:security.backupKey'),
    },
    {
      id: ENABLE_2FA_TABS.ENABLE_2FA,
      label: t('account:security.enable2fa'),
    },
  ];

  const tabIndexMap = {
    [ENABLE_2FA_TABS.DOWNLOAD_APP]: 0,
    [ENABLE_2FA_TABS.SCAN_QR]: 1,
    [ENABLE_2FA_TABS.BACKUP_KEY]: 2,
    [ENABLE_2FA_TABS.ENABLE_2FA]: 3,
  };

  const handleTabChange = (_event, newIndex: number) => {
    const tabId = Object.keys(tabIndexMap).find(key => tabIndexMap[key] === newIndex);
    if (tabId && activeTab !== tabId) {
      setActiveTab(tabId as SecurityTabType);
    }
  };

  const downloadAppTabRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeTab === ENABLE_2FA_TABS.DOWNLOAD_APP && downloadAppTabRef.current) {
      setTimeout(() => {
        // added because the scrollIntoView was not working when back button was clicked
        downloadAppTabRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        });
      }, 300);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== ENABLE_2FA_TABS.DOWNLOAD_APP) {
      setActiveTab(ENABLE_2FA_TABS.DOWNLOAD_APP);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    (<StylesProvider injectFirst>
      <Tabs
        TabIndicatorProps={{
          children: <span data-palette="SecurityTabs" />,
        }}
        classes={{
          root: styles.tabsContainer,
          indicator: styles.tabIndicator,
        }}
        value={tabIndexMap?.[activeTab] ?? 0}
        onChange={handleTabChange}
        aria-label="security tabs"
        variant={whenAboveMd ? 'fullWidth' : 'scrollable'}
        scrollButtons="off"
        data-testid="security-tabs"
      >
        {tabList.map(({ id, label }) => (
          <Tab
            ref={id === ENABLE_2FA_TABS.DOWNLOAD_APP ? downloadAppTabRef : null}
            classes={{
              root: styles.tabButton,
              selected: styles.selected,
              wrapper: styles.tabLabel,
            }}
            label={label}
            value={tabIndexMap[id]}
            key={id}
            id={`security-tab-${id}`}
            aria-controls={`security-tab-${id}`}
          />
        ))}
      </Tabs>
    </StylesProvider>)
  );
};

export default SecurityTabs;
